import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { cva } from 'class-variance-authority';
import { cn } from '../lib/utils.js';
const dotVariants = cva([
    'relative z-10 inline-flex h-[14px] w-[14px] shrink-0 items-center justify-center rounded-full',
    'border-2 border-line-strong bg-surface text-white',
].join(' '), {
    variants: {
        state: {
            done: 'border-success bg-success',
            current: 'border-accent bg-accent shadow-[0_0_0_4px_var(--color-accent-soft)]',
            future: '',
        },
    },
    defaultVariants: { state: 'future' },
});
const labelVariants = cva('min-w-0 flex-1 font-ui text-[13.5px] font-medium leading-[1.4] tracking-[-0.005em] text-ink-2', {
    variants: {
        state: {
            done: '',
            current: 'font-semibold text-ink',
            future: 'text-ink-3',
        },
    },
    defaultVariants: { state: 'future' },
});
const dateVariants = cva('whitespace-nowrap text-right font-mono text-xs leading-[1.4] text-ink-3', {
    variants: {
        state: {
            done: '',
            current: '',
            future: 'text-ink-4',
        },
    },
    defaultVariants: { state: 'future' },
});
export function LifecycleRail({ milestones = [], next = null }) {
    return (_jsxs("div", { className: "flex flex-col gap-4", children: [next && (_jsxs("div", { className: "flex items-end justify-between gap-4 border-b-[0.5px] border-line pb-4", children: [_jsxs("div", { className: "flex min-w-0 flex-col gap-1.5", children: [_jsx("span", { className: "font-ui text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-4", children: "Next" }), _jsx("span", { className: "font-ui text-[22px] font-semibold leading-[1.2] tracking-[-0.02em] text-ink", children: next.headline }), next.note && (_jsx("span", { className: "font-ui text-[12.5px] leading-[1.5] text-ink-4", children: next.note }))] }), next.date && (_jsx("span", { className: "whitespace-nowrap font-mono text-[12.5px] font-medium leading-[1.4] text-ink-3", children: next.date }))] })), _jsx("ol", { className: "m-0 flex list-none flex-col p-0", children: milestones.map((m, idx) => {
                    const state = m.state || 'future';
                    const isFirst = idx === 0;
                    const isLast = idx === milestones.length - 1;
                    // A connector between dot i and dot i+1 is "traversed" (accent) once
                    // dot i+1 is reached (done/current). Each row draws only the half of a
                    // connector that lives inside it — from its own top/bottom edge to the
                    // dot's centre — so the geometry never depends on a neighbour's height
                    // and stays aligned when a label wraps onto two lines (finding #5).
                    const reached = state === 'done' || state === 'current';
                    const nextState = milestones[idx + 1]?.state || 'future';
                    const nextReached = nextState === 'done' || nextState === 'current';
                    return (_jsxs("li", { "data-state": state, className: "relative flex items-center gap-3 py-[9px]", children: [!isFirst && (_jsx("span", { "aria-hidden": "true", className: cn('absolute left-[6px] top-0 h-1/2 w-0.5 rounded-pill bg-line', reached && 'bg-accent opacity-40') })), !isLast && (_jsx("span", { "aria-hidden": "true", className: cn('absolute bottom-0 left-[6px] h-1/2 w-0.5 rounded-pill bg-line', nextReached && 'bg-accent opacity-40') })), _jsx("span", { className: dotVariants({ state }), "aria-hidden": "true", children: state === 'done' && (_jsx("svg", { width: "9", height: "9", viewBox: "0 0 14 14", fill: "none", children: _jsx("path", { d: "M2.5 7.5l3 3 6-6.5", stroke: "currentColor", strokeWidth: "2.4", strokeLinecap: "round", strokeLinejoin: "round" }) })) }), _jsx("span", { className: labelVariants({ state }), children: m.label }), _jsx("span", { className: dateVariants({ state }), children: m.date || '—' })] }, m.label));
                }) })] }));
}
//# sourceMappingURL=lifecycle-rail.js.map